import { useEffect, useState } from 'react';
import axios from 'axios';

/* [CF 2026-08-17 #11.1] Show a car's BRAND and MODEL in whichever language the
 * site is currently set to — regardless of which language they were saved in.
 *
 * The brand and model on a car row are stored as plain text. They are chosen
 * from a dropdown, so the value is always a real master entry — but it is saved
 * in whatever language the owner happened to be using at the time. Measured on
 * production:
 *
 *              stored in Arabic   stored in Latin
 *   rentalCars        95               191
 *   cars             123               141
 *   carSales         118                10
 *
 * So neither language can be assumed, and translating one way only is not
 * enough: an English visitor was seeing تويوتا / كورولا on the rental pages,
 * and an Arabic visitor was seeing CAMRY on the sale pages. Both directions are
 * needed, which is why this maps by VALUE rather than by language.
 *
 * Both masters carry complete Arabic (108/108 brands, 1263/1263 models), so the
 * pairing always exists — it just has to be looked up. Each master is fetched
 * once per page load and cached at module level, so a grid of cards makes one
 * request rather than one per card.
 *
 * Anything that does not match a master entry is returned untouched, so an
 * unusual or misspelt value still displays instead of disappearing.
 */

const API = 'https://api.carsavenue.com/api';

const norm = (v) => String(v ?? '').trim().toLowerCase();

let cache = null;   // { byValue: Map<normalised, {en, ar}> }
let inFlight = null;

const hasArabic = (v) => /[\u0600-\u06FF]/.test(String(v ?? ''));

/* The master itself is partly polluted: some rows have the ARABIC name typed
   into the English column, so "كورولا" exists both as the good row
   (Corolla / كورولا) and a bad duplicate (كورولا / كورولا). Indexing blindly
   meant last-one-wins, and an English visitor could still be handed Arabic.
   A pair whose English side is genuinely Latin always beats one where it is
   not. */
const better = (candidate, existing) => {
  if (!existing) return true;
  const candOk = !hasArabic(candidate.en);
  const exOk = !hasArabic(existing.en);
  if (candOk !== exOk) return candOk;
  return false; // equally good — keep the first, for stability
};

function buildIndex(rows, enKey, arKey, index) {
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const en = String(row?.[enKey] ?? '').trim();
    const ar = String(row?.[arKey] ?? '').trim();
    if (!en && !ar) return;
    const pair = { en: en || ar, ar: ar || en };
    // Index under BOTH spellings so a lookup succeeds whichever was stored.
    [en, ar].filter(Boolean).forEach((key) => {
      const k = norm(key);
      if (better(pair, index.get(k))) index.set(k, pair);
    });
  });
}

function loadNameIndex() {
  if (cache) return Promise.resolve(cache);
  if (inFlight) return inFlight;

  const pick = (res) =>
    res?.data?.response?.data || res?.data?.response || res?.data?.data || [];

  inFlight = Promise.all([
    // No brandId -> the whole model master (1263 rows).
    axios.get(`${API}/admin/brand/carModel/listing`).then(pick).catch(() => []),
    axios.get(`${API}/admin/brand/listing`).then(pick).catch(() => []),
  ])
    .then(([models, brands]) => {
      const byValue = new Map();
      buildIndex(models, 'name', 'nameAr', byValue);
      buildIndex(brands, 'name', 'nameAr', byValue);
      cache = { byValue };
      return cache;
    })
    .catch(() => {
      cache = { byValue: new Map() };
      return cache;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * @param {boolean} isRTL  true when the site is in Arabic
 * @returns {(value:string) => string} the name in the ACTIVE language
 */
export function useCarNameLabel(isRTL) {
  const [index, setIndex] = useState(cache);

  useEffect(() => {
    // Needed in BOTH languages: English pages hold Arabic values too.
    if (cache) {
      setIndex(cache);
      return undefined;
    }
    let alive = true;
    loadNameIndex().then((c) => {
      if (alive) setIndex(c);
    });
    return () => {
      alive = false;
    };
  }, []);

  return (value) => {
    const raw = String(value ?? '');
    if (!raw.trim()) return raw;
    const idx = index || cache;
    if (!idx) return raw;
    const hit = idx.byValue.get(norm(raw));
    if (!hit) return raw;
    return isRTL ? hit.ar : hit.en;
  };
}

export default useCarNameLabel;
